import React, { useState, useRef, useEffect, useCallback } from 'react';

const C = {
  blue: '#0052cc',
  blueHov: '#2979e8',
  white: '#ffffff',
  green: '#16a34a',
  red: '#dc2626',
  amber: '#fbbf24',
  g50: '#f9fafb',
  g100: '#f3f4f6',
  g200: '#e5e7eb',
  g400: '#9ca3af',
  g500: '#6b7280',
  g700: '#374151',
  g900: '#111827',
};

const API = 'https://api.perfect.my/api/instant-install/v1';

const COUNTRIES = [
  { code: 'MY', name: 'Malaysia',        dial: '+60',  flag: '🇲🇾' },
  { code: 'SG', name: 'Singapore',       dial: '+65',  flag: '🇸🇬' },
  { code: 'ID', name: 'Indonesia',       dial: '+62',  flag: '🇮🇩' },
  { code: 'TH', name: 'Thailand',        dial: '+66',  flag: '🇹🇭' },
  { code: 'PH', name: 'Philippines',     dial: '+63',  flag: '🇵🇭' },
  { code: 'BN', name: 'Brunei',          dial: '+673', flag: '🇧🇳' },
  { code: 'MM', name: 'Myanmar',         dial: '+95',  flag: '🇲🇲' },
  { code: 'VN', name: 'Vietnam',         dial: '+84',  flag: '🇻🇳' },
  { code: 'KH', name: 'Cambodia',        dial: '+855', flag: '🇰🇭' },
  { code: 'CN', name: 'China',           dial: '+86',  flag: '🇨🇳' },
  { code: 'JP', name: 'Japan',           dial: '+81',  flag: '🇯🇵' },
  { code: 'KR', name: 'South Korea',     dial: '+82',  flag: '🇰🇷' },
  { code: 'IN', name: 'India',           dial: '+91',  flag: '🇮🇳' },
  { code: 'PK', name: 'Pakistan',        dial: '+92',  flag: '🇵🇰' },
  { code: 'BD', name: 'Bangladesh',      dial: '+880', flag: '🇧🇩' },
  { code: 'LK', name: 'Sri Lanka',       dial: '+94',  flag: '🇱🇰' },
  { code: 'NP', name: 'Nepal',           dial: '+977', flag: '🇳🇵' },
  { code: 'AU', name: 'Australia',       dial: '+61',  flag: '🇦🇺' },
  { code: 'NZ', name: 'New Zealand',     dial: '+64',  flag: '🇳🇿' },
  { code: 'GB', name: 'United Kingdom',  dial: '+44',  flag: '🇬🇧' },
  { code: 'US', name: 'United States',   dial: '+1',   flag: '🇺🇸' },
  { code: 'CA', name: 'Canada',          dial: '+1',   flag: '🇨🇦' },
  { code: 'DE', name: 'Germany',         dial: '+49',  flag: '🇩🇪' },
  { code: 'FR', name: 'France',          dial: '+33',  flag: '🇫🇷' },
  { code: 'NL', name: 'Netherlands',     dial: '+31',  flag: '🇳🇱' },
  { code: 'SA', name: 'Saudi Arabia',    dial: '+966', flag: '🇸🇦' },
  { code: 'AE', name: 'UAE',             dial: '+971', flag: '🇦🇪' },
  { code: 'QA', name: 'Qatar',           dial: '+974', flag: '🇶🇦' },
  { code: 'KW', name: 'Kuwait',          dial: '+965', flag: '🇰🇼' },
  { code: 'OM', name: 'Oman',            dial: '+968', flag: '🇴🇲' },
  { code: 'BH', name: 'Bahrain',         dial: '+973', flag: '🇧🇭' },
  { code: 'JO', name: 'Jordan',          dial: '+962', flag: '🇯🇴' },
  { code: 'EG', name: 'Egypt',           dial: '+20',  flag: '🇪🇬' },
  { code: 'ZA', name: 'South Africa',    dial: '+27',  flag: '🇿🇦' },
  { code: 'NG', name: 'Nigeria',         dial: '+234', flag: '🇳🇬' },
];

const STEPS = ['Unit & Package', 'Details', 'Documents', 'Review'];

function StepIndicator({ current }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, marginBottom: 32, overflowX: 'auto', padding: '4px 0' }}>
      {STEPS.map((s, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <React.Fragment key={s}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, minWidth: 64 }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                background: done ? C.green : active ? C.blue : C.g200,
                color: done || active ? C.white : C.g400,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 700,
                border: active ? `3px solid ${C.blueHov}` : 'none',
                boxShadow: active ? '0 0 0 3px rgba(0,82,204,0.15)' : 'none',
                transition: 'all 0.3s',
                flexShrink: 0,
              }}>
                {done ? '✓' : i + 1}
              </div>
              <span style={{ fontSize: 11, color: active ? C.blue : done ? C.green : C.g400, fontWeight: active ? 700 : 500, whiteSpace: 'nowrap' }}>{s}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ flex: 1, height: 2, background: done ? C.green : C.g200, margin: '0 4px', marginBottom: 20, minWidth: 16, transition: 'background 0.3s' }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function Card({ children, style = {} }) {
  return (
    <div style={{
      background: C.white,
      borderRadius: 16,
      boxShadow: '0 2px 12px rgba(0,0,0,.08)',
      padding: '20px 20px',
      ...style,
    }}>
      {children}
    </div>
  );
}

function Spinner({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2.5" />
      <path d="M8 2a6 6 0 0 1 6 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <animateTransform attributeName="transform" type="rotate" from="0 8 8" to="360 8 8" dur="0.65s" repeatCount="indefinite" />
      </path>
    </svg>
  );
}

function Btn({ children, onClick, variant = 'primary', disabled = false, loading = false, style = {}, type = 'button' }) {
  const [hov, setHov] = useState(false);
  const isDisabled = disabled || loading;
  const base = {
    primary: { background: isDisabled ? C.g200 : hov ? C.blueHov : C.blue, color: isDisabled ? C.g400 : C.white, border: 'none' },
    secondary: { background: hov ? C.g100 : C.white, color: C.blue, border: `1.5px solid ${C.blue}` },
    danger: { background: hov ? '#b91c1c' : C.red, color: C.white, border: 'none' },
    ghost: { background: hov ? C.g100 : 'transparent', color: C.g700, border: `1px solid ${C.g200}` },
  }[variant];
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={isDisabled}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        ...base,
        borderRadius: 10,
        padding: '12px 24px',
        fontSize: 15,
        fontWeight: 600,
        fontFamily: "'Inter', sans-serif",
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.18s',
        display: 'inline-flex', alignItems: 'center', gap: 8,
        ...style,
      }}>
      {loading && <Spinner />}
      {children}
    </button>
  );
}

function Input({ label, value, onChange, placeholder, type = 'text', required, error, hint }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <label style={{ fontSize: 13, fontWeight: 600, color: C.g700, display: 'block', marginBottom: 6 }}>
        {label}{required && <span style={{ color: C.red, marginLeft: 3 }}>*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        style={{
          width: '100%', boxSizing: 'border-box', padding: '11px 14px',
          border: `1.5px solid ${error ? C.red : C.g200}`,
          borderRadius: 10, fontSize: 16, color: C.g900,
          fontFamily: "'Inter', sans-serif",
          outline: 'none', transition: 'border 0.2s',
          background: C.white,
        }}
        onFocus={e => e.target.style.borderColor = C.blue}
        onBlur={e => e.target.style.borderColor = error ? C.red : C.g200}
      />
      {hint && !error && <p style={{ fontSize: 12, color: C.g400, margin: '4px 0 0' }}>{hint}</p>}
      {error && <p style={{ fontSize: 12, color: C.red, margin: '4px 0 0' }}>{error}</p>}
    </div>
  );
}

function Select({ label, value, onChange, options, required, error, placeholder }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <label style={{ fontSize: 13, fontWeight: 600, color: C.g700, display: 'block', marginBottom: 6 }}>
        {label}{required && <span style={{ color: C.red, marginLeft: 3 }}>*</span>}
      </label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          width: '100%', boxSizing: 'border-box', padding: '11px 14px',
          border: `1.5px solid ${error ? C.red : C.g200}`,
          borderRadius: 10, fontSize: 16, color: value ? C.g900 : C.g400,
          fontFamily: "'Inter', sans-serif",
          outline: 'none', background: C.white, cursor: 'pointer',
        }}>
        <option value="">{placeholder || 'Select...'}</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {error && <p style={{ fontSize: 12, color: C.red, margin: '4px 0 0' }}>{error}</p>}
    </div>
  );
}

function Alert({ type = 'error', children }) {
  const colors = {
    error: { bg: '#fef2f2', border: C.red, text: C.red },
    success: { bg: '#f0fdf4', border: C.green, text: C.green },
    info: { bg: '#eff6ff', border: C.blue, text: C.blue },
    warn: { bg: '#fffbeb', border: C.amber, text: '#92400e' },
  }[type];
  return (
    <div style={{ background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 10, padding: '12px 16px', color: colors.text, fontSize: 14, marginBottom: 16 }}>
      {children}
    </div>
  );
}

function PhoneInput({ label, onChange, required, error, hint }) {
  const [dialCode, setDialCode] = useState('+60');
  const [number, setNumber] = useState('');

  const notify = (dial, num) => onChange(dial + num);

  return (
    <div style={{ marginBottom: 20 }}>
      <label style={{ fontSize: 13, fontWeight: 600, color: C.g700, display: 'block', marginBottom: 6 }}>
        {label}{required && <span style={{ color: C.red, marginLeft: 3 }}>*</span>}
      </label>
      <div style={{ display: 'flex', gap: 8 }}>
        <select
          value={dialCode}
          onChange={e => { setDialCode(e.target.value); notify(e.target.value, number); }}
          autoComplete="off"
          style={{
            padding: '11px 10px', border: `1.5px solid ${error ? C.red : C.g200}`,
            borderRadius: 10, fontSize: 16, color: C.g900, background: C.white,
            fontFamily: "'Inter', sans-serif", cursor: 'pointer', flexShrink: 0,
            outline: 'none',
          }}>
          {COUNTRIES.map(c => (
            <option key={c.code} value={c.dial}>{c.flag} {c.dial}</option>
          ))}
        </select>
        <input
          type="tel"
          value={number}
          onChange={e => { const n = e.target.value.replace(/\D/g, '').replace(/^0+/, ''); setNumber(n); notify(dialCode, n); }}
          placeholder="e.g. 193750293"
          autoComplete="off"
          style={{
            flex: 1, boxSizing: 'border-box', padding: '11px 14px',
            border: `1.5px solid ${error ? C.red : C.g200}`,
            borderRadius: 10, fontSize: 16, color: C.g900,
            fontFamily: "'Inter', sans-serif",
            outline: 'none', background: C.white, transition: 'border 0.2s',
          }}
          onFocus={e => e.target.style.borderColor = C.blue}
          onBlur={e => e.target.style.borderColor = error ? C.red : C.g200}
        />
      </div>
      {hint && !error && <p style={{ fontSize: 12, color: C.g400, margin: '4px 0 0' }}>{hint}</p>}
      {error && <p style={{ fontSize: 12, color: C.red, margin: '4px 0 0' }}>{error}</p>}
    </div>
  );
}

// ──────────── STEP 0: Login ────────────
function LoginStep({ onDone }) {
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async () => {
    if (!phone || !/^\+\d{7,15}$/.test(phone)) { setError('Please enter a valid phone number.'); return; }
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams({ phone: phone.trim() });
      const res = await fetch(`${API}/promoters/verify?${params}`, { headers: { Accept: 'application/json' } });
      const json = await res.json();
      if (res.status === 200 && json.success) {
        onDone({
          promoterId: json.data.id,
          promoterPhone: phone,
          promoterName: json.data.name,
          promoterEmail: json.data.email,
        });
      } else if (res.status === 403) {
        setError('Your promoter account is inactive. Please contact your supervisor.');
      } else if (res.status === 404) {
        setError('Phone number not found. Please use your registered promoter number.');
      } else if (res.status === 422) {
        const msgs = json.errors ? Object.values(json.errors).flat().join(' ') : json.message;
        setError(msgs || 'Please enter a valid phone number.');
      } else {
        setError(json.message || 'Unable to verify. Please try again.');
      }
    } catch {
      setError('Unable to connect. Please check your connection and try again.');
    } finally { setLoading(false); }
  };

  return (
    <Card>
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <div style={{ width: 64, height: 64, borderRadius: 16, background: C.blue, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', boxShadow: '0 4px 16px rgba(0,82,204,.25)' }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={C.white} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
          </svg>
        </div>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: C.g900, margin: '0 0 8px' }}>Promoter Login</h2>
        <p style={{ color: C.g500, fontSize: 15, margin: 0 }}>Enter your registered phone number to continue</p>
      </div>
      {error && <Alert type="error">{error}</Alert>}
      <PhoneInput label="Phone Number" onChange={setPhone} required hint="Use the phone number registered to your promoter account" />
      <Btn onClick={handleLogin} loading={loading} style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}>
        {loading ? 'Verifying…' : 'Continue →'}
      </Btn>
    </Card>
  );
}

// ──────────── STEP 1: Location ────────────
function LocationStep({ onDone, onBack, promoterPhone }) {
  const [areas, setAreas] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true); setError('');
    fetch(`${API}/locations?${new URLSearchParams({ promoter_phone: promoterPhone })}`, { headers: { Accept: 'application/json' } })
      .then(r => r.json())
      .then(j => { if (j.success) setAreas(j.data); else setError(j.message || 'Failed to load locations'); })
      .catch(() => setError('Unable to load locations. Please check your connection and try again.'))
      .finally(() => setLoading(false));
  }, [promoterPhone]);

  const filtered = areas.filter(a =>
    !search || a.name.toLowerCase().includes(search.toLowerCase()) ||
    a.city?.toLowerCase().includes(search.toLowerCase())
  );

  // For single-block areas, address.id is known immediately; for multi-block it's selected in UnitPackageStep
  const handleSelect = (area) => {
    if (!area.has_multiple_blocks) {
      onDone({ ...area, addressId: area.address.id, selectedAddress: area.address });
    } else {
      onDone({ ...area });
    }
  };

  return (
    <Card>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: C.g900, margin: '0 0 6px' }}>Choose Your Building</h2>
      <p style={{ color: C.g500, fontSize: 14, margin: '0 0 20px' }}>Select the building where you'd like to register</p>
      <Input label="Search" value={search} onChange={setSearch} placeholder="Search by building name or city…" />
      {error && <Alert type="error">{error}</Alert>}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '32px 0', color: C.g400 }}>Loading locations…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '32px 0', color: C.g400 }}>No locations found.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 400, overflowY: 'auto', paddingRight: 4 }}>
          {filtered.map(area => (
            <div key={area.id} onClick={() => handleSelect(area)} style={{
              border: `2px solid ${C.g200}`, borderRadius: 12, padding: '14px 16px', cursor: 'pointer',
              background: C.white, transition: 'all 0.18s', display: 'flex', alignItems: 'flex-start', gap: 14,
            }}>
              <span style={{ fontSize: 26, lineHeight: 1, marginTop: 2 }}>{area.has_multiple_blocks ? '🏢' : '🏡'}</span>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 700, color: C.g900, fontSize: 15 }}>{area.name}</span>
                  {area.has_multiple_blocks && (
                    <span style={{ fontSize: 11, background: '#eff6ff', color: C.blue, padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>Multiple blocks</span>
                  )}
                </div>
                <p style={{ margin: '3px 0 0', fontSize: 12, color: C.g400 }}>{area.city}, {area.state} · {area.zone?.name}</p>
              </div>
              <div style={{ color: C.g200, fontSize: 18, marginTop: 2 }}>›</div>
            </div>
          ))}
        </div>
      )}
      <div style={{ marginTop: 20 }}>
        <Btn variant="ghost" onClick={onBack}>← Back</Btn>
      </div>
    </Card>
  );
}

function BlockSearch({ blocks, loading, error, selected, onSelect, fieldError }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);


  const filtered = blocks.filter(b =>
    !query || b.name.toLowerCase().includes(query.toLowerCase())
  );

  const handleSelect = (blk) => { onSelect(blk); setQuery(blk.name); setOpen(false); };
  const handleFocus = () => { setQuery(''); setOpen(true); };
  const handleBlur = () => { setTimeout(() => { setOpen(false); if (selected) setQuery(selected.name); }, 150); };

  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ fontSize: 13, fontWeight: 600, color: C.g700, display: 'block', marginBottom: 6 }}>
        Block <span style={{ color: C.red }}>*</span>
      </label>
      {error && <Alert type="error">{error}</Alert>}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '12px 0', color: C.g400, fontSize: 13 }}>Loading blocks…</div>
      ) : (
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            value={query}
            autoComplete="off"
            placeholder="Select your block…"
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={e => { setQuery(e.target.value); setOpen(true); if (!e.target.value) onSelect(null); }}
            style={{
              width: '100%', boxSizing: 'border-box', padding: '11px 14px',
              border: `1.5px solid ${fieldError ? C.red : open ? C.blue : C.g200}`,
              borderRadius: 10, fontSize: 16, color: C.g900,
              fontFamily: "'Inter', sans-serif",
              outline: 'none', background: C.white, transition: 'border 0.2s',
            }}
          />
          {open && filtered.length > 0 && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
              background: C.white, border: `1.5px solid ${C.g200}`, borderRadius: 10,
              boxShadow: '0 4px 16px rgba(0,0,0,.1)', marginTop: 4,
              maxHeight: 220, overflowY: 'auto',
            }}>
              {filtered.map(blk => (
                <div
                  key={blk.id}
                  onMouseDown={() => handleSelect(blk)}
                  style={{
                    padding: '11px 14px', cursor: 'pointer', fontSize: 14,
                    color: selected?.id === blk.id ? C.blue : C.g900,
                    fontWeight: selected?.id === blk.id ? 700 : 400,
                    background: selected?.id === blk.id ? '#eff6ff' : C.white,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    borderBottom: `1px solid ${C.g100}`,
                  }}
                >
                  <span>{blk.name}</span>
                  {selected?.id === blk.id && <span style={{ color: C.blue }}>✓</span>}
                </div>
              ))}
            </div>
          )}
          {open && filtered.length === 0 && query && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
              background: C.white, border: `1.5px solid ${C.g200}`, borderRadius: 10,
              boxShadow: '0 4px 16px rgba(0,0,0,.1)', marginTop: 4,
              padding: '12px 14px', fontSize: 13, color: C.g400,
            }}>
              No blocks found
            </div>
          )}
        </div>
      )}
      {fieldError && <p style={{ fontSize: 12, color: C.red, margin: '6px 0 0' }}>{fieldError}</p>}
    </div>
  );
}

// ──────────── STEP 2: Unit & Package ────────────
function UnitPackageStep({ onDone, onBack, location, promoterPhone }) {
  // Block selection (only for multi-block locations)
  const [blocks, setBlocks] = useState([]);
  const [blocksLoading, setBlocksLoading] = useState(false);
  const [blocksError, setBlocksError] = useState('');
  const [selectedBlock, setSelectedBlock] = useState(null);

  const [level, setLevel] = useState('');
  const [unit, setUnit] = useState('');
  const [packages, setPackages] = useState([]);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [proceeding, setProceeding] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (location.has_multiple_blocks) {
      setBlocksLoading(true); setBlocksError('');
      fetch(`${API}/location-addresses?${new URLSearchParams({ promoter_phone: promoterPhone, area_id: location.id })}`, { headers: { Accept: 'application/json' } })
        .then(r => r.json())
        .then(j => { if (j.success) setBlocks(j.data); else setBlocksError(j.message || 'Failed to load blocks'); })
        .catch(() => setBlocksError('Unable to load blocks. Please check your connection and try again.'))
        .finally(() => setBlocksLoading(false));
    }
  }, [location.id, location.has_multiple_blocks, promoterPhone]);

  const [pkgError, setPkgError] = useState('');
  useEffect(() => {
    setLoading(true);
    fetch(`${API}/packages`, { headers: { Accept: 'application/json' } })
      .then(r => r.json())
      .then(j => {
        if (j.success) {
          setPackages(j.data);
          // Auto-select the highest-duration group
          const seen = {};
          const groups = [];
          j.data.forEach(pkg => {
            const key = pkg.contract_package?.id ?? 'none';
            if (!seen[key]) {
              seen[key] = true;
              const months = parseInt((pkg.contract_package?.name || '0').replace(/\D/g, ''), 10) || 0;
              groups.push({ key, months });
            }
          });
          groups.sort((a, b) => b.months - a.months);
          if (groups.length) setSelectedGroup(groups[0].key);
        } else {
          setPkgError(j.message || 'Failed to load packages');
        }
      })
      .catch(() => setPkgError('Unable to load packages. Please check your connection and try again.'))
      .finally(() => setLoading(false));
  }, []);

  const activeAddress = location.has_multiple_blocks ? selectedBlock : location.selectedAddress;

  const validate = () => {
    const e = {};
    if (location.has_multiple_blocks && !selectedBlock) e.block = 'Please select your block';
    if (activeAddress?.building_type !== 'single_tenant' && !level.trim()) e.level = 'Level is required';
    if (!unit.trim()) e.unit = 'Unit number is required';
    if (!selectedPkg) e.pkg = 'Please select a package';
    setErrors(e);
    return Object.keys(e).length === 0;
  };


  return (
    <Card>
      <div style={{ marginBottom: 8 }}>
        <span style={{ fontSize: 12, color: C.g400, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Installing at</span>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: C.g900, margin: '2px 0 0' }}>{location.name}</h3>
        <p style={{ fontSize: 13, color: C.g500, margin: 0 }}>{`${location.city}, ${location.state}`}</p>
      </div>
      <hr style={{ border: 'none', borderTop: `1px solid ${C.g200}`, margin: '20px 0' }} />

      <h3 style={{ fontSize: 16, fontWeight: 700, color: C.g900, margin: '0 0 16px' }}>Your Unit</h3>

      {/* Block picker — searchable autocomplete */}
      {location.has_multiple_blocks && (
        <BlockSearch
          blocks={blocks}
          loading={blocksLoading}
          error={blocksError}
          selected={selectedBlock}
          onSelect={blk => setSelectedBlock(blk)}
          fieldError={errors.block}
        />
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {activeAddress?.building_type !== 'single_tenant' && (
          <Input label="Level" value={level} onChange={setLevel} placeholder="e.g. 12" required error={errors.level} />
        )}
        <Input label="Unit" value={unit} onChange={setUnit} placeholder="e.g. 3" required error={errors.unit} />
      </div>

      <h3 style={{ fontSize: 16, fontWeight: 700, color: C.g900, margin: '24px 0 12px' }}>Choose a Package</h3>
      {errors.pkg && <Alert type="error">{errors.pkg}</Alert>}
      {pkgError && <Alert type="error">{pkgError}</Alert>}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '24px 0', color: C.g400 }}>Loading packages…</div>
      ) : (() => {
        const seen = {};
        const groups = [];
        packages.forEach(pkg => {
          const key = pkg.contract_package?.id ?? 'none';
          if (!seen[key]) {
            seen[key] = true;
            const months = parseInt((pkg.contract_package?.name || '0').replace(/\D/g, ''), 10) || 0;
            groups.push({ key, label: months === 0 ? 'No contract' : (pkg.contract_package?.name || 'No contract'), months, pkgs: [] });
          }
          groups.find(g => g.key === key).pkgs.push(pkg);
        });
        groups.sort((a, b) => b.months - a.months);
        groups.forEach(g => g.pkgs.sort((a, b) => parseFloat(b.price) - parseFloat(a.price)));
        const activeGroup = groups.find(g => g.key === selectedGroup) || groups[0];

        return (
          <>
            {/* Duration tab buttons */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, overflowX: 'auto', paddingBottom: 4 }}>
              {groups.map(g => {
                const isActive = g.key === (activeGroup?.key);
                return (
                  <button
                    key={g.key}
                    onClick={() => setSelectedGroup(g.key)}
                    style={{
                      flexShrink: 0,
                      padding: '8px 18px',
                      borderRadius: 14,
                      border: `2px solid ${isActive ? C.blue : C.g200}`,
                      background: isActive ? C.blue : C.white,
                      color: isActive ? C.white : C.g700,
                      cursor: 'pointer', transition: 'all 0.18s',
                      fontFamily: "'Inter', sans-serif",
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
                    }}
                  >
                    <span style={{ fontSize: 15, fontWeight: 800, lineHeight: 1.2 }}>{g.label}</span>
                    <span style={{ fontSize: 11, fontWeight: 500, opacity: isActive ? 0.85 : 0.6, letterSpacing: 0.2 }}>{g.months === 0 ? 'packages' : 'contract'}</span>
                  </button>
                );
              })}
            </div>

            {activeGroup?.months === 0 && (
              <div style={{ background: '#fffbeb', border: `1px solid ${C.amber}`, borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#92400e', fontWeight: 500 }}>
                ⚠️ RM 500 installation fee is required.
              </div>
            )}

            {/* Packages for the active group */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {(activeGroup?.pkgs || []).map(pkg => {
                const isSelected = selectedPkg?.id === pkg.id;
                return (
                  <div
                    key={pkg.id}
                    onClick={() => setSelectedPkg(pkg)}
                    style={{
                      border: `2px solid ${isSelected ? C.blue : C.g200}`,
                      borderRadius: 14, padding: '16px 20px', cursor: 'pointer',
                      background: isSelected ? '#eff6ff' : C.white,
                      transition: 'all 0.18s',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <span style={{ fontWeight: 700, fontSize: 16, color: C.g900 }}>{pkg.name}</span>
                        <p style={{ margin: '4px 0 8px', fontSize: 13, color: C.g500 }}>{pkg.description}</p>
                        <p style={{ margin: 0, fontSize: 12, color: C.g500 }}>
                          ↓ {pkg.download_speed} Mbps · ↑ {pkg.upload_speed} Mbps
                        </p>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 16 }}>
                        <div style={{ fontSize: 22, fontWeight: 800, color: isSelected ? C.blue : C.g900 }}>
                          RM{parseFloat(pkg.price).toFixed(2)}
                        </div>
                        <div style={{ fontSize: 12, color: C.g400 }}>/month</div>
                        {activeGroup?.months > 0 && parseFloat(pkg.installation_charge) === 0 && (
                          <div style={{ fontSize: 11, color: C.green, fontWeight: 700, marginTop: 4 }}>FREE installation</div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        );
      })()}

      <div style={{ display: 'flex', gap: 12, marginTop: 28 }}>
        {onBack && <Btn variant="ghost" onClick={onBack}>← Back</Btn>}
        <Btn loading={proceeding} onClick={() => { if (validate()) { setProceeding(true); onDone({ level, unit, package: selectedPkg, addressId: activeAddress?.id, selectedAddress: activeAddress }); } }} style={{ flex: 1, justifyContent: 'center' }}>
          Continue →
        </Btn>
      </div>
    </Card>
  );
}

// ──────────── STEP 3: Customer Details ────────────
function DetailsStep({ onDone, onBack }) {
  const [form, setForm] = useState({ name: '', phone: '', email: '', idType: 'ic', idNumber: '', idCountry: 'SG' });
  const [errors, setErrors] = useState({});
  const [proceeding, setProceeding] = useState(false);
  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));

  const handleIcChange = (val) => {
    const digits = val.replace(/\D/g, '').slice(0, 12);
    let formatted = digits;
    if (digits.length > 6) formatted = `${digits.slice(0, 6)}-${digits.slice(6)}`;
    if (digits.length > 8) formatted = `${digits.slice(0, 6)}-${digits.slice(6, 8)}-${digits.slice(8)}`;
    setForm(f => ({ ...f, idNumber: formatted }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Full name is required';
    if (!form.phone || !/^\+\d{7,15}$/.test(form.phone)) e.phone = 'Please enter a valid phone number';
    if (!form.email.trim()) e.email = 'Email address is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Please enter a valid email address';
    if (form.idType === 'passport' && !form.idCountry) e.idCountry = 'Please select a country';
    if (!form.idNumber.trim()) e.idNumber = 'Identification number is required';
    if (form.idType === 'ic' && form.idNumber && !/^\d{12}$/.test(form.idNumber.replace(/-/g, '')))
      e.idNumber = 'IC number must be 12 digits';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  return (
    <Card>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: C.g900, margin: '0 0 6px' }}>Customer Details</h2>
      <p style={{ color: C.g500, fontSize: 14, margin: '0 0 28px' }}>Enter the customer's personal information</p>

      <Input label="Full Name" value={form.name} onChange={set('name')} placeholder="As per IC or passport" required error={errors.name} />
      <PhoneInput label="Phone Number" onChange={v => set('phone')(v)} required error={errors.phone} />
      <Input label="Email Address" value={form.email} onChange={set('email')} placeholder="customer@example.com" type="email" required error={errors.email} />

      <div style={{ marginBottom: 20 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: C.g700, display: 'block', marginBottom: 8 }}>Identification Type <span style={{ color: C.red }}>*</span></label>
        <div style={{ display: 'flex', gap: 10 }}>
          {[{ value: 'ic', label: '🪪 MyKad (IC)' }, { value: 'passport', label: '🛂 Passport' }].map(opt => (
            <div
              key={opt.value}
              onClick={() => setForm(f => ({ ...f, idType: opt.value, idNumber: '' }))}
              style={{
                flex: 1, padding: '11px 16px', borderRadius: 10, cursor: 'pointer',
                border: `2px solid ${form.idType === opt.value ? C.blue : C.g200}`,
                background: form.idType === opt.value ? '#eff6ff' : C.white,
                fontSize: 14, fontWeight: 600, color: form.idType === opt.value ? C.blue : C.g700,
                textAlign: 'center', transition: 'all 0.18s',
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      </div>

      {form.idType === 'passport' && (
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: C.g700, display: 'block', marginBottom: 6 }}>
            Issuing Country <span style={{ color: C.red }}>*</span>
          </label>
          <select
            value={form.idCountry}
            onChange={e => set('idCountry')(e.target.value)}
            autoComplete="off"
            style={{
              width: '100%', boxSizing: 'border-box', padding: '11px 14px',
              border: `1.5px solid ${errors.idCountry ? C.red : C.g200}`,
              borderRadius: 10, fontSize: 16, color: C.g900,
              fontFamily: "'Inter', sans-serif", outline: 'none', background: C.white, cursor: 'pointer',
            }}>
            {COUNTRIES.filter(c => c.code !== 'MY').map(c => (
              <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
            ))}
          </select>
          {errors.idCountry && <p style={{ fontSize: 12, color: C.red, margin: '4px 0 0' }}>{errors.idCountry}</p>}
        </div>
      )}

      <Input
        label={form.idType === 'ic' ? 'IC Number' : 'Passport Number'}
        value={form.idNumber}
        onChange={form.idType === 'ic' ? handleIcChange : set('idNumber')}
        placeholder={form.idType === 'ic' ? 'e.g. 900101-01-1234' : 'e.g. A12345678'}
        required
        error={errors.idNumber}
        hint={form.idType === 'ic' ? 'Dashes will be added automatically' : 'As shown on your passport'}
      />

      <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
        <Btn variant="ghost" onClick={onBack}>← Back</Btn>
        <Btn loading={proceeding} onClick={() => { if (validate()) { setProceeding(true); onDone(form); } }} style={{ flex: 1, justifyContent: 'center' }}>Continue →</Btn>
      </div>
    </Card>
  );
}

// ──────────── STEP 4: Documents (IC scan only) ────────────
function DocumentsStep({ onDone, onBack, idType }) {
  const [icFile, setIcFile] = useState(null);
  const [icPreview, setIcPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [proceeding, setProceeding] = useState(false);

  const [showCamera, setShowCamera] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);

  const startCamera = async () => {
    setCameraError('');
    setShowCamera(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch (err) {
      setCameraError('Camera access denied. Please upload a photo instead.');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob(blob => {
      const file = new File([blob], 'ic-photo.jpg', { type: 'image/jpeg' });
      setIcFile(file);
      setIcPreview(canvas.toDataURL('image/jpeg'));
      stopCamera();
    }, 'image/jpeg', 0.92);
  };

  const handleIcUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setErrors(er => ({ ...er, ic: 'File must be under 5MB' })); return; }
    setIcFile(file);
    const reader = new FileReader();
    reader.onload = ev => setIcPreview(ev.target.result);
    reader.readAsDataURL(file);
    setErrors(er => ({ ...er, ic: '' }));
  };

  const validate = () => {
    const e = {};
    if (!icFile) e.ic = `Please ${idType === 'ic' ? 'scan or upload' : 'upload'} your ${idType === 'ic' ? 'IC' : 'passport'} photo`;
    setErrors(e);
    if (Object.keys(e).length) return;
    onDone({ icPhoto: icFile });
  };

  return (
    <Card>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: C.g900, margin: '0 0 6px' }}>Documents</h2>
      <p style={{ color: C.g500, fontSize: 14, margin: '0 0 28px' }}>Scan or upload the customer's {idType === 'ic' ? 'IC' : 'passport'}</p>

      <div style={{ marginBottom: 8 }}>
        <label style={{ fontSize: 14, fontWeight: 700, color: C.g900, display: 'block', marginBottom: 12 }}>
          {idType === 'ic' ? '🪪 IC Photo' : '🛂 Passport Photo'} <span style={{ color: C.red }}>*</span>
        </label>
        {errors.ic && <Alert type="error">{errors.ic}</Alert>}

        {icPreview ? (
          <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', border: `2px solid ${C.green}` }}>
            <img src={icPreview} alt="IC" style={{ width: '100%', maxHeight: 200, objectFit: 'cover', display: 'block' }} />
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.opacity = 1}
              onMouseLeave={e => e.currentTarget.style.opacity = 0}>
              <Btn variant="secondary" onClick={() => { setIcFile(null); setIcPreview(null); }} style={{ fontSize: 13 }}>Retake</Btn>
            </div>
            <div style={{ position: 'absolute', top: 8, right: 8, background: C.green, color: C.white, borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>✓ Captured</div>
          </div>
        ) : showCamera ? (
          <div style={{ borderRadius: 12, overflow: 'hidden', background: '#000', position: 'relative' }}>
            {cameraError ? (
              <Alert type="error">{cameraError}</Alert>
            ) : (
              <>
                <video ref={videoRef} autoPlay playsInline style={{ width: '100%', maxHeight: 260, display: 'block', objectFit: 'cover' }} />
                <canvas ref={canvasRef} style={{ display: 'none' }} />
                <div style={{ display: 'flex', gap: 10, padding: '12px', justifyContent: 'center', background: '#111' }}>
                  <Btn onClick={capturePhoto} style={{ flex: 1, justifyContent: 'center' }}>📷 Capture</Btn>
                  <Btn variant="ghost" onClick={stopCamera} style={{ color: C.white, borderColor: C.white }}>Cancel</Btn>
                </div>
              </>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn variant="secondary" onClick={startCamera} style={{ flex: 1, justifyContent: 'center', padding: '16px' }}>
              📷 Scan with Camera
            </Btn>
            <label style={{ flex: 1 }}>
              <div style={{ border: `1.5px dashed ${C.g300 || C.g400}`, borderRadius: 10, padding: '16px', textAlign: 'center', cursor: 'pointer', color: C.g500, fontSize: 14, background: C.g50 }}>
                ⬆ Upload File
              </div>
              <input type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={handleIcUpload} style={{ display: 'none' }} />
            </label>
          </div>
        )}
        <p style={{ fontSize: 12, color: C.g400, margin: '6px 0 0' }}>JPG, PNG or PDF · max 5MB · must show all details clearly</p>
      </div>

      <div style={{ display: 'flex', gap: 12, marginTop: 28 }}>
        <Btn variant="ghost" onClick={onBack}>← Back</Btn>
        <Btn loading={proceeding} onClick={async () => { setProceeding(true); await validate(); setProceeding(false); }} style={{ flex: 1, justifyContent: 'center' }}>Continue →</Btn>
      </div>
    </Card>
  );
}

// ──────────── STEP 5: T&C + Review + Signature ────────────
const TNC_TEXT = `1. This service agreement is between the customer ("You") and Perfect Network Sdn. Bhd. ("We", "Us").

2. By signing up, you agree to a minimum contract period as specified in your chosen package.

3. Early termination before the contract period ends may incur an early termination fee.

4. Monthly billing begins from the date of successful installation. Payment is due within 14 days of invoice date.

5. Internet speeds are provided on a best-effort basis and may vary based on network conditions, device capabilities, and usage patterns.

6. You are responsible for ensuring the accuracy of all information provided during registration. False or misleading information may result in service suspension.

7. We reserve the right to suspend or terminate service for breach of these terms, non-payment, or misuse of the network.

8. Customer data will be handled in accordance with the Personal Data Protection Act 2010 (Malaysia).

9. Technical support is available during business hours. Emergency support is available for critical service disruptions.

10. This agreement is governed by the laws of Malaysia.`;

function ReviewStep({ onDone, onBack, data, submitting, submitError }) {
  const [tncAgreed, setTncAgreed] = useState(false);
  const tncRef = useRef(null);

  // Signature
  const sigCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [sigEmpty, setSigEmpty] = useState(true);
  const [sigMode, setSigMode] = useState('draw');
  const [sigFile, setSigFile] = useState(null);
  const [sigError, setSigError] = useState('');
  const lastPos = useRef(null);


  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches?.[0] || e;
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const canvas = sigCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.beginPath(); ctx.moveTo(pos.x, pos.y);
    lastPos.current = pos;
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = sigCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.strokeStyle = C.g900; ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    ctx.lineTo(pos.x, pos.y); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(pos.x, pos.y);
    lastPos.current = pos;
    setSigEmpty(false);
    setSigError('');
  };

  const endDraw = (e) => { e.preventDefault(); setIsDrawing(false); lastPos.current = null; };

  const clearSig = () => {
    const canvas = sigCanvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    setSigEmpty(true); setSigFile(null);
  };

  const handleSubmit = async () => {
    let sig = sigFile;
    if (sigMode === 'draw') {
      if (sigEmpty) { setSigError('Please provide the customer\'s signature'); return; }
      const canvas = sigCanvasRef.current;
      sig = await new Promise(res => canvas.toBlob(blob => res(new File([blob], 'signature.png', { type: 'image/png' })), 'image/png'));
    } else {
      if (!sigFile) { setSigError('Please upload the customer\'s signature'); return; }
    }
    onDone(sig);
  };

  const { location, unitPackage, customer } = data;

  const Row = ({ label, value }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: `1px solid ${C.g100}` }}>
      <span style={{ fontSize: 13, color: C.g500 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: C.g900, textAlign: 'right', maxWidth: '60%' }}>{value}</span>
    </div>
  );

  return (
    <Card>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: C.g900, margin: '0 0 6px' }}>Review & Confirm</h2>
      <p style={{ color: C.g500, fontSize: 14, margin: '0 0 24px' }}>Please review all details before submitting the order</p>

      {/* Details */}
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ fontSize: 13, fontWeight: 700, color: C.g700, textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 4px' }}>Customer Information</h4>
        <Row label="Name" value={customer.name} />
        <Row label="Phone" value={customer.phone} />
        {customer.email && <Row label="Email" value={customer.email} />}
        <Row label="ID Type" value={customer.idType === 'ic' ? 'MyKad (IC)' : 'Passport'} />
        {customer.idType === 'passport' && <Row label="Issuing Country" value={COUNTRIES.find(c => c.code === customer.idCountry)?.name || customer.idCountry} />}
        <Row label="ID Number" value={customer.idNumber} />
      </div>

      <div style={{ marginBottom: 24 }}>
        <h4 style={{ fontSize: 13, fontWeight: 700, color: C.g700, textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 4px' }}>Address</h4>
        <Row label="Building" value={location.name} />
        {location.has_multiple_blocks && unitPackage.selectedAddress?.name && <Row label="Block" value={unitPackage.selectedAddress.name} />}
        {unitPackage.level && <Row label="Level" value={unitPackage.level} />}
        <Row label="Unit" value={unitPackage.unit} />
      </div>

      {/* T&C */}
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ fontSize: 13, fontWeight: 700, color: C.g900, margin: '0 0 10px' }}>Terms & Conditions</h4>
        <div
          ref={tncRef}
          style={{ border: `1.5px solid ${C.g200}`, borderRadius: 10, padding: '16px', maxHeight: 200, overflowY: 'auto', fontSize: 13, color: C.g700, lineHeight: 1.7, background: C.g50, whiteSpace: 'pre-line' }}
        >
          {TNC_TEXT}
        </div>
      </div>

      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: 24 }}>
        <input
          type="checkbox"
          checked={tncAgreed}
          onChange={e => setTncAgreed(e.target.checked)}
          style={{ marginTop: 3, width: 18, height: 18, flexShrink: 0, cursor: 'pointer', accentColor: C.blue }}
        />
        <span style={{ fontSize: 14, color: C.g700, lineHeight: 1.5 }}>
          I have read, understood, and agree to the Terms & Conditions. The information I have provided is accurate and correct.
        </span>
      </label>

      {/* Signature — shown after checkbox is ticked */}
      {tncAgreed && (
        <div style={{ marginBottom: 24, borderTop: `1px solid ${C.g200}`, paddingTop: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <label style={{ fontSize: 14, fontWeight: 700, color: C.g900 }}>✍️ Customer Signature <span style={{ color: C.red }}>*</span></label>
            <div style={{ display: 'flex', gap: 6 }}>
              {['draw', 'upload'].map(m => (
                <button key={m} onClick={() => { setSigMode(m); setSigFile(null); setSigError(''); }}
                  style={{
                    padding: '5px 12px', fontSize: 12, fontWeight: 600, borderRadius: 8, cursor: 'pointer',
                    border: `1.5px solid ${sigMode === m ? C.blue : C.g200}`,
                    background: sigMode === m ? '#eff6ff' : C.white,
                    color: sigMode === m ? C.blue : C.g500,
                  }}>
                  {m === 'draw' ? '✏️ Draw' : '⬆ Upload'}
                </button>
              ))}
            </div>
          </div>
          {sigError && <Alert type="error">{sigError}</Alert>}

          {sigMode === 'draw' ? (
            <div>
              <div style={{ border: `1.5px solid ${C.g200}`, borderRadius: 12, background: '#fafafa', position: 'relative', overflow: 'hidden' }}>
                <canvas
                  ref={sigCanvasRef}
                  width={480}
                  height={160}
                  style={{ display: 'block', width: '100%', height: 160, cursor: 'crosshair', touchAction: 'none' }}
                  onMouseDown={startDraw}
                  onMouseMove={draw}
                  onMouseUp={endDraw}
                  onMouseLeave={endDraw}
                  onTouchStart={startDraw}
                  onTouchMove={draw}
                  onTouchEnd={endDraw}
                />
                {sigEmpty && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                    <span style={{ color: C.g400, fontSize: 14 }}>Sign here…</span>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
                <button onClick={clearSig} style={{ fontSize: 12, color: C.g500, background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px' }}>
                  🗑 Clear
                </button>
              </div>
            </div>
          ) : (
            <div>
              {sigFile ? (
                <div style={{ border: `2px solid ${C.green}`, borderRadius: 10, padding: 12, display: 'flex', alignItems: 'center', gap: 10, background: '#f0fdf4' }}>
                  <span style={{ fontSize: 24 }}>✅</span>
                  <div>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: C.g900 }}>{sigFile.name}</p>
                    <p style={{ margin: 0, fontSize: 12, color: C.g500 }}>{(sigFile.size / 1024).toFixed(0)} KB</p>
                  </div>
                  <button onClick={() => setSigFile(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: C.red, cursor: 'pointer', fontSize: 18 }}>×</button>
                </div>
              ) : (
                <label>
                  <div style={{ border: `1.5px dashed ${C.g400}`, borderRadius: 10, padding: '24px', textAlign: 'center', cursor: 'pointer', color: C.g500, fontSize: 14 }}>
                    ⬆ Upload signature image<br /><span style={{ fontSize: 12 }}>JPG or PNG · max 2MB</span>
                  </div>
                  <input type="file" accept=".jpg,.jpeg,.png" onChange={e => {
                    const f = e.target.files[0];
                    if (f && f.size <= 2 * 1024 * 1024) { setSigFile(f); setSigError(''); }
                    else if (f) setSigError('File must be under 2MB');
                  }} style={{ display: 'none' }} />
                </label>
              )}
            </div>
          )}
        </div>
      )}

      {submitError && <Alert type="error">{submitError}</Alert>}

      <div style={{ display: 'flex', gap: 12 }}>
        <Btn variant="ghost" onClick={onBack} disabled={submitting}>← Back</Btn>
        <Btn
          onClick={handleSubmit}
          disabled={!tncAgreed}
          loading={submitting}
          style={{ flex: 1, justifyContent: 'center', background: tncAgreed && !submitting ? C.green : undefined }}
        >
          {submitting ? 'Submitting…' : '✅ Submit Order'}
        </Btn>
      </div>
    </Card>
  );
}

// ──────────── Success ────────────
function SuccessScreen({ order, onReset }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ width: 80, height: 80, borderRadius: '50%', background: '#f0fdf4', border: `3px solid ${C.green}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 36 }}>
        ✅
      </div>
      <h2 style={{ fontSize: 26, fontWeight: 800, color: C.g900, margin: '0 0 8px' }}>Order Submitted!</h2>
      <p style={{ color: C.g500, fontSize: 15, margin: '0 0 28px' }}>Installation is scheduled for today</p>

      <Card style={{ textAlign: 'left', marginBottom: 24 }}>
        <div style={{ textAlign: 'center', marginBottom: 4 }}>
          <div style={{ fontSize: 12, color: C.g400, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Order Number</div>
          <div style={{ fontSize: 32, fontWeight: 800, color: C.blue, fontFamily: "'DM Mono', monospace", letterSpacing: 3 }}>{order.order_number}</div>
        </div>
        <hr style={{ border: 'none', borderTop: `1px solid ${C.g200}`, margin: '16px 0' }} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <div style={{ fontSize: 12, color: C.g400 }}>Customer</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.g900 }}>{order.customer_name}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: C.g400 }}>Phone</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.g900 }}>{order.customer_phone_number}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: C.g400 }}>Location</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.g900 }}>{order.address?.name}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: C.g400 }}>Unit</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.g900 }}>{[order.block_no, order.floor_no && `Level ${order.floor_no}`, order.unit_no].filter(Boolean).join(', ')}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: C.g400 }}>Installation Date</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.g900 }}>{new Date(order.installation_date).toLocaleDateString('en-MY', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: C.g400 }}>Promoter</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.g900 }}>{order.promoter?.name}</div>
          </div>
        </div>
      </Card>

      <Alert type="success">
        The installer will contact the you shortly. Please keep the order number for reference.
      </Alert>

      <Btn onClick={onReset} variant="secondary" style={{ marginTop: 8, width: '100%', justifyContent: 'center' }}>
        + Submit Another Order
      </Btn>
    </div>
  );
}

// ──────────── Main App ────────────
export default function App() {
  // step 0–3 = order form steps (Unit&Package, Details, Documents, Review)
  const [step, setStep] = useState(0);

  // Promoter and location are persisted in localStorage — only cleared by logout
  const [promoter, setPromoter] = useState(() => {
    try { return JSON.parse(localStorage.getItem('pi_promoter')); } catch { return null; }
  });
  const [location, setLocation] = useState(() => {
    try { return JSON.parse(localStorage.getItem('pi_location')); } catch { return null; }
  });

  const [unitPackage, setUnitPackage] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [docs, setDocs] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [order, setOrder] = useState(null);

  // Hidden logout: 5 taps on the copyright text
  const copyrightClickCount = useRef(0);
  const copyrightTimer = useRef(null);

  const savePromoter = (p) => {
    localStorage.setItem('pi_promoter', JSON.stringify(p));
    setPromoter(p);
  };

  const saveLocation = (l) => {
    localStorage.setItem('pi_location', JSON.stringify(l));
    setLocation(l);
  };

  const logout = () => {
    localStorage.removeItem('pi_promoter');
    localStorage.removeItem('pi_location');
    copyrightClickCount.current = 0;
    if (copyrightTimer.current) clearTimeout(copyrightTimer.current);
    setPromoter(null); setLocation(null);
    setStep(0); setUnitPackage(null); setCustomer(null);
    setDocs(null); setSubmitting(false); setSubmitError(''); setOrder(null);
  };

  // "Submit Another Order" — keeps promoter + location, resets the order form only
  const reset = () => {
    setStep(0); setUnitPackage(null); setCustomer(null);
    setDocs(null); setSubmitting(false); setSubmitError(''); setOrder(null);
  };

  const handleCopyrightClick = () => {
    if (copyrightTimer.current) clearTimeout(copyrightTimer.current);
    copyrightClickCount.current += 1;
    if (copyrightClickCount.current >= 5) {
      logout();
    } else {
      copyrightTimer.current = setTimeout(() => { copyrightClickCount.current = 0; }, 3000);
    }
  };

  const submitOrder = async (sigFile) => {
    setSubmitting(true); setSubmitError('');
    try {
      const fd = new FormData();
      fd.append('promoter_phone', promoter.promoterPhone);
      fd.append('customer_name', customer.name);
      fd.append('customer_phone', customer.phone);
      if (customer.email) fd.append('customer_email', customer.email);
      fd.append('customer_identification_type', customer.idType);
      fd.append('customer_identification_number', customer.idNumber.replace(/-/g, ''));
      if (customer.idType === 'passport') fd.append('customer_identification_country', customer.idCountry);
      fd.append('customer_identification_photo', docs.icPhoto);
      fd.append('customer_signature', sigFile);
      fd.append('address_id', unitPackage.addressId || location.addressId);
      fd.append('package_id', unitPackage.package.id);
      fd.append('unit_number', unitPackage.unit);
      if (unitPackage.level) fd.append('floor', unitPackage.level);

      const res = await fetch(`${API}/orders`, { method: 'POST', body: fd, headers: { Accept: 'application/json' } });
      const json = await res.json();

      if (res.status === 201 && json.success) {
        setOrder(json.data);
      } else if (res.status === 403) {
        setSubmitError('Promoter phone number not recognised or inactive. Please check and try again.');
      } else if (res.status === 422) {
        const msgs = json.errors ? Object.values(json.errors).flat().join(' ') : json.message;
        setSubmitError(msgs || 'Validation failed. Please check all fields.');
      } else {
        setSubmitError(json.message || 'An unexpected error occurred. Please try again.');
      }
    } catch {
      setSubmitError('Unable to submit order. Please check your connection and try again.');
    } finally { setSubmitting(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: C.g50, fontFamily: "'Inter', sans-serif" }}>
      {/* Header */}
      <div style={{ background: C.blue, padding: '0 24px' }}>
        <div style={{ maxWidth: 640, margin: '0 auto', padding: '14px 0', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <img src="/logo.svg" alt="Perfect Internet" style={{ height: 36 }} />
        </div>
      </div>


      {/* Main content */}
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '16px 12px 40px' }}>

        {/* ── Setup: Promoter login ── */}
        {!promoter && <LoginStep onDone={d => savePromoter(d)} />}

        {/* ── Setup: Location selection ── */}
        {promoter && !location && (
          <LocationStep
            onDone={d => saveLocation(d)}
            onBack={() => { localStorage.removeItem('pi_promoter'); setPromoter(null); }}
            promoterPhone={promoter.promoterPhone}
          />
        )}

        {/* ── Order form steps (0–3) ── */}
        {promoter && location && !order && (
          <>
            <StepIndicator current={step} />
            {step === 0 && (
              <UnitPackageStep
                onDone={d => { setUnitPackage(d); setStep(1); }}
                onBack={null}
                location={location}
                promoterPhone={promoter?.promoterPhone}
              />
            )}
            {step === 1 && (
              <DetailsStep
                onDone={d => { setCustomer(d); setStep(2); }}
                onBack={() => setStep(0)}
              />
            )}
            {step === 2 && (
              <DocumentsStep
                onDone={d => { setDocs(d); setStep(3); }}
                onBack={() => setStep(1)}
                idType={customer?.idType}
              />
            )}
            {step === 3 && (
              <ReviewStep
                onDone={submitOrder}
                onBack={() => setStep(2)}
                data={{ location, unitPackage, customer }}
                submitting={submitting}
                submitError={submitError}
              />
            )}
          </>
        )}

        {/* ── Success ── */}
        {promoter && location && order && <SuccessScreen order={order} onReset={reset} />}
      </div>

      {/* Footer — click 5× to trigger logout */}
      <div
        onClick={handleCopyrightClick}
        style={{ padding: '4px 24px 28px', textAlign: 'center', fontSize: 12, color: C.g400, cursor: 'default', userSelect: 'none' }}
      >
        © {new Date().getFullYear()} Perfect Network Sdn. Bhd. 202201026777 (1472474-K)
      </div>
    </div>
  );
}
